<template>
  <div class="pl-2 pr-2">
    <div class="columns is-centered">
      <div class="column is-10">
        <div class="columns cc-is-hidden-print is-multiline is-mobile">
          <div class="column is-8-mobile">
            <button class="button mr-2 is-fullwidth" v-on:click="this.initializePrint()">{{ $t("i18n.global.buttons.generateReport") }}</button>
          </div>
          <div class="column is-4-mobile">
            <router-link to="/manual" class="button mr-2 is-fullwidth" target="_blank">{{ $t("i18n.global.buttons.openManual") }}</router-link>
          </div>
          <div class="column is-8-mobile">
            <button class="button mr-2 is-fullwidth" v-on:click="this.toggleAdvancedInfo()">
              <span>{{ $t("i18n.global.buttons.toggleAdvancedInfo") }}</span>
              <label class="switch ml-2">
                <input type="checkbox" v-model="this.showAdvancedInformation" v-on:click="this.toggleAdvancedInfo()">
                <span class="slider round"></span>
              </label>
            </button>
          </div>
          <div class="column is-4-mobile is-narrow">
            <div class="select is-fullwidth">
              <select v-model="$i18n.locale" v-on:change="this.fetchData()">
                <option v-for="locale in $i18n.availableLocales" :key="`locale-${locale}`" :value="locale">{{ locale[0].toUpperCase() + locale.slice(1).toLowerCase() }}</option>
              </select>
            </div>
          </div>
        </div>
        <div v-if="this.system">
          <div class="block mb-0">
            <h1 class="title mb-0">{{ this.system.organisation }} - {{ this.system.name }}
              <h5 class="subtitle is-5 mb-0" v-if="this.system.description !== 'DESCRIPTION'"> {{ this.system.description }} </h5>
              <h6 class="subtitle is-5"> {{ this.startDate }} - {{ this.endDate }} </h6>
            </h1>
          </div>
          <div class="columns is-multiline cc-is-hidden-print cc-is-printable mt-2" v-bind:class="{ 'is-hidden': !this.showAdvancedInformation }">
            <div class="column is-12">
              <div class="panel cc-panel">
                <p class="panel-heading">{{ $t("i18n.systemList.header")  }}</p>
                  <div class="panel-block cc-x-scroll">
                  <table class="table is-fullwidth" v-if="this.dataAggregatedPerUnit">
                    <thead>
                      <th> {{ $t("i18n.systemList.name") }} </th>
                      <th> {{ $t("i18n.systemList.manufacturer") }} </th>
                      <th> {{ $t("i18n.systemList.model") }} </th>
                      <th> {{ $t("i18n.systemList.application") }} </th>
                      <th> {{ $t("i18n.systemList.category") }} </th>
                      <th> {{ $t("i18n.systemList.size") }} </th>
                      <th> {{ $t("i18n.systemList.cooling") }} </th>
                      <th> {{ $t("i18n.systemList.heating") }} </th>
                      <th> {{ $t("i18n.systemList.energyTotal") }} </th>
                      <th> {{ $t("i18n.systemList.coolingCOPTotal") }} </th>
                      <th> {{ $t("i18n.systemList.heatingCOPTotal") }} </th>
                    </thead>
                    <tbody>
                    <tr  v-for="unit in this.system.units" v-bind:key="unit.name">
                      <td> {{ unit.name }} </td>
                      <td> {{ unit.manufacturer }} </td>
                      <td> {{ unit.model }} </td>
                      <td> {{ this.$t('i18n.unitApplications.' + unit.application ) }} </td>
                      <td> {{ this.$t('i18n.unitCategories.' + unit.category ) }} </td>
                      <td> {{ unit.sizeUnits }} {{ unit.size }} </td>
                      <td> {{ this.totalsPerUnit(unit.id)?.cooling }} </td>
                      <td> {{ this.totalsPerUnit(unit.id)?.heating }} </td>
                      <td> {{ this.totalsPerUnit(unit.id)?.energyTotal }} </td>
                      <td> {{ this.totalsPerUnit(unit.id)?.coolingCOPTotal }} </td>
                      <td> {{ this.totalsPerUnit(unit.id)?.heatingCOPTotal }} </td>
                    </tr>
                    </tbody>
                    <tfoot><tr></tr></tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="columns is-centered cc-is-hidden-print">
          <div class="column">
            <ccError ref="ccError"></ccError>
          </div>
        </div>
        <div class="columns cc-is-chart-row cc-is-hidden-print cc-is-printable">
          <div class="column is-4">
            <ccDistributionChart
              v-bind:idChartDiv="'ccDistributionChart'"
              v-if="this.data"
              v-bind:totals="this.totals"
              v-bind:chartDivInlineStyle="calculateChartDivInlineStyle"
            ></ccDistributionChart>
          </div>
          <div class="column is-8">
            <ccUsedAndGenerated
              v-bind:idChartDiv="'ccUsedAndGenerated'"
              v-if="this.data && this.totalsPerUnitType"
              v-bind:totals="this.totals"
              v-bind:unitApplicationTotals="this.totalsPerUnitType"
              v-bind:chartDivInlineStyle="calculateChartDivInlineStyle"
            ></ccUsedAndGenerated>
          </div>
        </div>
        <div class="columns cc-is-chart-row cc-is-hidden-print cc-is-printable">
          <div class="column is-12">
            <ccEnergyPerTime
              v-bind:idChartDiv="'ccEnergyPerTime'"
              v-if="this.data"
              v-bind:data="this.data"
            ></ccEnergyPerTime>
          </div>
        </div>
        <div class="columns cc-is-chart-row cc-is-hidden-print cc-is-printable">
          <div class="column is-4">
            <ccTemperatureOccurrence
              v-bind:idChartDiv="'ccTemperatureOccurrence'"
              v-if="this.rawData && this.system"
              v-bind:data="this.rawData"
              v-bind:noOfProcesses="this.system.processes.length"
            ></ccTemperatureOccurrence>
          </div>
            <div class="column is-8">
              <ccEnergyPerTemperature
              v-bind:idChartDiv="'ccEnergyPerTemperature'"
              v-if="this.dataAggregatedOnTemperature"
              v-bind:data="this.dataAggregatedOnTemperature"
              v-bind:xAxisDataField="this.temperatureAggregatedDatafieldxAxis"
            ></ccEnergyPerTemperature>
          </div>
        </div>
        <div class="columns cc-is-chart-row cc-is-hidden-print cc-is-printable">
          <div class="column is-4">
            <ccAverageCOP
              v-bind:idChartDiv="'ccAverageCOP'"
              v-if="this.data && this.totalsPerUnitType"
              v-bind:totals="this.totals"
              v-bind:unitApplicationTotals="this.totalsPerUnitType"
            ></ccAverageCOP>
          </div>
          <div class="column is-8">
            <ccCOPPerTemperature
              v-bind:idChartDiv="'ccCOPPerTemperature'"
              v-if="this.dataAggregatedOnTemperature && this.system"
              v-bind:system="this.system"
              v-bind:data="this.rawData"
              v-bind:xAxisDataField="this.temperatureAggregatedDatafieldxAxis"
            ></ccCOPPerTemperature>
          </div>
        </div>
        <div v-if="!this.system">
          System not fetched. Please contact support.
        </div>
        <!-- Modal for loading a spinner -->
        <div id="cc-loading-mask" class="modal" v-bind:class="{ 'is-active': this.showLoadMaskModal }">
          <div class="modal-background"></div>
          <div class="modal-content has-text-centered is-flex is-justify-content-center">
            <p class="image is-128x128">
              <img src="@/assets/rotgear5.gif" alt="">
            </p>
          </div>
        </div>
        <!-- div only used for printing -->
        <div id="printArea"></div>
      </div>
    </div>
  </div>
</template>
<script>
import { System } from '@/classes/System'
import ccHelpers from '@/components/ccHelpers'
import html2canvas from 'html2canvas'

export default {
  data: function () {
    return {
      data: null,
      rawData: null,
      dataAggregatedOnTemperature: null,
      dataAggregatedPerUnit: null,
      signature: null,
      showLoadMask: false,
      showAdvancedInformation: false,
      temperatureAggregatedDatafieldxAxis: 'outdoorTemperatureRounded'
    }
  },
  computed: {
    system: function () {
      if (this.$store.state.system) {
        return this.$store.state.system
      } else {
        return null
      }
    },
    user: function () {
      const user = {
        name: null,
        password: null,
        authenticated: false,
        idSystem: null // the system this user is currently authenticated for.
      }

      if (localStorage.getItem('ccUsername') !== '') {
        user.name = localStorage.getItem('ccUsername')
      }

      if (localStorage.getItem('ccPassword') !== '') {
        user.password = localStorage.getItem('ccPassword')
      }

      user.idSystem = parseInt(localStorage.getItem('ccSystem'))
      user.authenticated = (localStorage.getItem('ccAuthenticated') === 'true')

      return user
    },
    paramIdSystem: function () {
      return parseInt(this.$route.params.idSystem)
    },
    endDate: function () {
      const endDate = new Date(this.$route.params.endDate)
      return endDate.toISOString().split('T')[0]
    },
    startDate: function () {
      const endDate = new Date(this.endDate)
      const startDate = new Date(this.endDate)
      startDate.setMonth(endDate.getMonth() - 3)
      return startDate.toISOString().split('T')[0]
    },
    totals: function () {
      const totals = {
        compressor: 0,
        auxiliary: 0,
        heating: 0,
        cooling: 0,
        signature: 0,
        compressorCoolingCop: null,
        totalCoolingCop: null,
        compressorHeatingCop: null,
        totalHeatingCop: null
      }

      this.data.forEach(dataPoint => {
        totals.compressor += dataPoint.energyComp
        totals.auxiliary += dataPoint.energyAux
        totals.heating += dataPoint.energyHeating
        totals.cooling += dataPoint.energyCooling
        totals.signature += dataPoint.signatureEnergyTot
      })

      totals.compressor = parseFloat(totals.compressor.toFixed(0))
      totals.auxiliary = parseFloat(totals.auxiliary.toFixed(0))
      totals.heating = parseFloat(totals.heating.toFixed(0))
      totals.cooling = parseFloat(totals.cooling.toFixed(0))
      totals.signature = parseFloat(totals.signature.toFixed(0))

      if (totals.compressor > 0) {
        totals.compressorCoolingCop = parseFloat((totals.cooling / totals.compressor).toFixed(2))
        totals.compressorHeatingCop = parseFloat((totals.heating / totals.compressor).toFixed(2))
      } else {
        totals.compressorCoolingCop = 0
        totals.compressorHeatingCop = 0
      }

      if ((totals.auxiliary + totals.compressor) > 0) {
        totals.totalCoolingCop = parseFloat((totals.cooling / (totals.auxiliary + totals.compressor)).toFixed(2))
        totals.totalHeatingCop = parseFloat((totals.heating / (totals.auxiliary + totals.compressor)).toFixed(2))
      } else {
        totals.totalCoolingCop = 0
        totals.totalHeatingCop = 0
      }

      return totals
    },
    totalsPerUnitType: function () {
      const totals = []
      this.dataAggregatedPerUnit.forEach(dataPoint => {
        const unitApplication = this.system.units.find(unit => unit.id === dataPoint.idUnit)?.application
        const totalForApplication = totals.find(total => total.application === unitApplication)
        if (totalForApplication) {
          totalForApplication.energyComp += dataPoint.energyComp
          totalForApplication.energyAux += dataPoint.energyAux
          totalForApplication.heating += dataPoint.energyHeating
          totalForApplication.cooling += dataPoint.energyCooling
        } else {
          totals.push({
            application: unitApplication,
            energyComp: dataPoint.energyComp,
            energyAux: dataPoint.energyAux,
            heating: dataPoint.energyHeating,
            cooling: dataPoint.energyCooling
          })
        }
      })

      totals.forEach(total => {
        total.energyComp = parseFloat(total.energyComp.toFixed(0))
        total.energyAux = parseFloat(total.energyAux.toFixed(0))
        total.heating = parseFloat(total.heating.toFixed(0))
        total.cooling = parseFloat(total.cooling.toFixed(0))

        if (total.energyComp > 0) {
          total.compressorCoolingCop = parseFloat((total.cooling / total.energyComp).toFixed(2))
          total.compressorHeatingCop = parseFloat((total.heating / total.energyComp).toFixed(2))
        } else {
          total.compressorCoolingCop = 0
          total.compressorHeatingCop = 0
        }

        if ((total.energyAux + total.energyComp) > 0) {
          total.totalCoolingCop = parseFloat((total.cooling / (total.energyAux + total.energyComp)).toFixed(2))
          total.totalHeatingCop = parseFloat((total.heating / (total.energyAux + total.energyComp)).toFixed(2))
        } else {
          total.totalCoolingCop = 0
          total.totalHeatingCop = 0
        }
      })

      return totals
    },
    showLoadMaskModal: function () {
      return this.showLoadMask
    },
    /**
     *  Computed function which set the chart div height depending on the length of totals per unit type.
     *  If totalsPerUnitType array is more then two, we calculate the height.
     *  Else we will just return an empty string and use cc-chart instead.
     *  totalsPerUnitType represents the number of unit application types in the site. this will affect the number of columns in ccUsedAndGenerated.
     *  This calculation is required to adapt the height of ccUsedAndGenerated and ccDistribution based on the number of unit applications.
     */
    calculateChartDivInlineStyle: function () {
      if (this.totalsPerUnitType.length > 2) {
        const height = this.totalsPerUnitType.length * 100 + 50
        return `height: ${height}px;`
      } else {
        return '' // Empty inline style, the css-class declared will be used with it's default height.
      }
    }
  },
  mounted () {
    // Check if user is authenticated for this system already
    if (this.user.authenticated && this.user.idSystem === this.paramIdSystem) {
      if (this.system) {
        // system already fetched, get data
        this.fetchData()
      } else {
        // system not fetched, get it, then get data
        this.fetchSystem()
      }
    } else {
      // user need to login
      this.$router.push({ name: 'Login', params: { idSystem: this.paramIdSystem, endDate: this.endDate } })
    }
  },
  methods: {
    fetchSystem: function () {
      Promise.all([
        fetch(`integrations?cmd=edsystem&id=${this.user.idSystem}&user=${this.user.name}&password=${this.user.password}&format=json`).then(res => ccHelpers.isSuccess(res) || ccHelpers.handleFailure(res))
      ]).then(data => {
        const systemJson = data[0][0].systems[0]
        this.$store.commit('addSystem',
          new System(
            this.user.idSystem, systemJson.name, systemJson.description, systemJson.organisation, systemJson.category, systemJson.sizeUnits, systemJson.size,
            data[0][0].units,
            data[0][0].processes,
            data[0][0].profileschedules
          )
        )

        this.fetchData()
      })
    },
    fetchData: function () {
      // show mask before data load.
      this.toggleLoadingMask()

      // Clear data on reload
      this.rawData = null
      this.signature = null
      this.data = null
      this.dataAggregatedOnTemperature = null
      this.dataAggregatedPerUnit = null

      const interval = 'quarterly'
      Promise.all([
        fetch(`integrations?cmd=eddata&id=${this.system.id}&user=${this.user.name}&password=${this.user.password}&format=json&endtime=${this.endDate}&interval=${interval}`).then(res => ccHelpers.isSuccess(res) || ccHelpers.handleFailure(res)),
        fetch(`integrations?cmd=edsignature&id=${this.system.id}&user=${this.user.name}&password=${this.user.password}&format=json&endtime=${this.endDate}&interval=${interval}`).then(res => ccHelpers.isSuccess(res) || ccHelpers.handleFailure(res))
      ]).then(data => {
        if (data[0].length === 0) {
          this.$refs.ccError.newError('No Data', ['No data found for the specified system and interval.'], this.$route.name)
        }

        this.rawData = data[0]
        this.signature = data[1]

        // parse raw data into daily aggregate, also appends signature per day
        this.data = ccHelpers.aggregatePerDay(this.rawData, this.signature, this.system)
        this.dataAggregatedOnTemperature = ccHelpers.aggregatePerTemperature(this.rawData)
        this.dataAggregatedPerUnit = ccHelpers.aggregatePerUnit(this.rawData, this.system)

        // datafetching is complete, remove loading-mask
        this.toggleLoadingMask()
      }).catch(response => {
        this.$refs.ccError.newError('Error In data request', [response.message], this.$route.name)

        this.toggleLoadingMask()
        // TODO: implement whoopsie
      })
    },
    toggleAdvancedInfo: function () {
      this.showAdvancedInformation = !this.showAdvancedInformation
    },
    initializePrint: function () {
      // this function only shows spinner befor starting the printlogic.
      // show spinner svg
      this.toggleLoadingMask()

      // briefly pause to adjust DOM (show loader) befor setting up canvases for print
      setTimeout(() => { this.printWithHtml2Canvas() }, 10)
    },
    printWithHtml2Canvas: function () {
      // clear old print images
      const printArea = document.getElementById('printArea')
      printArea.innerHTML = ''

      // get all divs to be printed
      const printDivs = document.getElementsByClassName('cc-is-printable')

      // add a promise to generate a canvas-img for each printable div
      const printPromises = []
      for (let index = 0; index < printDivs.length; index++) {
        const element = printDivs[index]
        printPromises.push(html2canvas(element, {
          scale: 2
        }))
      }

      // run all promises, then prompt print
      Promise.all(printPromises).then(data => {
        data.forEach(canvas => {
          // add our print class to canvas
          canvas.classList.add('cc-print-row')
          // add our canvas to the printarea
          printArea.appendChild(canvas)
        })
        // promises are done
        // hide the loading-mask
        this.toggleLoadingMask()
        // show the print dialogue after breif wait.
        setTimeout(() => { window.print() }, 200)
      })
    },
    toggleLoadingMask: function () {
      this.showLoadMask = !this.showLoadMask
    },
    totalsPerUnit: function (idUnit) {
      const totals = {
        energyComp: 0,
        energyAux: 0,
        energyTotal: 0,
        heating: 0,
        cooling: 0,
        coolingCOPTotal: 0,
        heatingCOPTotal: 0
      }

      const unitData = this.dataAggregatedPerUnit.find(dataPoint => dataPoint.idUnit === idUnit)

      if (unitData) {
        totals.energyComp = parseFloat(unitData.energyComp.toFixed(0))
        totals.energyAux = parseFloat(unitData.energyAux.toFixed(0))
        totals.energyTotal = parseFloat((unitData.energyComp + unitData.energyAux).toFixed(0))
        totals.heating = parseFloat(unitData.energyHeating.toFixed(0))
        totals.cooling = parseFloat(unitData.energyCooling.toFixed(0))
      }

      if (totals.energyTotal > 0) {
        totals.coolingCOPTotal = parseFloat((totals.cooling / totals.energyTotal).toFixed(2))
        totals.heatingCOPTotal = parseFloat((totals.heating / totals.energyTotal).toFixed(2))
      } else {
        totals.coolingCOPTotal = 0
        totals.heatingCOPTotal = 0
      }

      return totals
    }
  }
}
</script>
